import { useDisclosure } from '@common/hooks'
import { MolsonBox, MolsonIcon, MolsonTypography } from '@components/reusable'
import {
  ClickAwayListener,
  type SxProps,
  styled,
  type Theme,
  Tooltip,
  type TooltipProps,
  tooltipClasses
} from '@mui/material'
import { molsonUseTheme } from '@theme/customMuiExports'

type TooltipConfig = {
  backgroundColor?: string
  description?: React.ReactNode
  title: string
  titleStyle?: SxProps<Theme>
  paragraphsDescription?: boolean
  paragraphs?: string[]
}

const CustomTooltip = styled(
  ({ className, ...props }: TooltipProps & { config?: TooltipConfig }) => (
    <Tooltip {...props} classes={{ popper: className }} />
  )
)(({ config, theme }) => ({
  '.MuiTooltip-arrow': {
    color: config?.backgroundColor || theme.palette.primary.lightest
  },
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: config?.backgroundColor || theme.palette.primary.lightest,
    borderRadius: '8px',
    color: theme.palette.secondary.dark,
    width: 245,
    padding: '12px'
  },
  '& .MuiTypography-root': {
    lineHeight: '16px',
    letterSpacing: '0.24px',
    fontSize: '12px'
  }
}))

const Container = styled('div')`
  display: flex;
  align-items: center;
`

export type MolsonCustomTooltipProps = Omit<
  TooltipProps,
  'children' | 'title'
> & {
  config: TooltipConfig
}

export const MolsonCustomTooltip = ({
  config,
  ...props
}: MolsonCustomTooltipProps) => {
  const [opened, { close, open }] = useDisclosure()
  const theme = molsonUseTheme()

  const handleClick = (e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    e.stopPropagation()
    open()
  }

  return (
    <ClickAwayListener onClickAway={close} mouseEvent="onMouseUp">
      <Container>
        <CustomTooltip
          PopperProps={{
            disablePortal: true,
            modifiers: [
              {
                name: 'offset',
                options: {
                  offset: [-10, 0]
                }
              }
            ]
          }}
          title={
            <MolsonBox
              display="flex"
              flexDirection="column"
              gap={config.description || config.paragraphsDescription ? 0.5 : 0}
            >
              <MolsonTypography fontWeight={700} sx={config.titleStyle}>
                {config.title}
              </MolsonTypography>
              {config?.description && (
                <MolsonTypography>{config?.description}</MolsonTypography>
              )}
              {config.paragraphsDescription && (
                <MolsonBox display="flex" flexDirection="column" gap={2}>
                  {config?.paragraphs?.map((paragraph, index) => (
                    <MolsonTypography key={index}>{paragraph}</MolsonTypography>
                  ))}
                </MolsonBox>
              )}
            </MolsonBox>
          }
          arrow={true}
          config={config}
          disableFocusListener={true}
          disableHoverListener={true}
          disableTouchListener={true}
          onClose={close}
          open={opened}
          placement="top-start"
          {...props}
        >
          <MolsonBox
            onClick={handleClick}
            style={{ cursor: 'pointer', display: 'flex' }}
          >
            <MolsonIcon
              sx={{
                path: {
                  fill: theme.palette.primary.light
                }
              }}
              variant="INFO_OUTLINE"
            />
          </MolsonBox>
        </CustomTooltip>
      </Container>
    </ClickAwayListener>
  )
}
